import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowRight, MapPin, Briefcase } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadGenIndustryLocation() {
  const { industry, location } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/lg/industry-location/${industry}/${location}`);
        setData(data);
        document.title = `${data.narrative?.hero_headline || "Lead Generation"} | WebsiteTech.pro`;
      } catch (e) {
        setError(e?.response?.data?.detail || "This combination isn't published yet.");
      } finally { setLoading(false); }
    })();
  }, [industry, location]);

  if (loading) return (
    <div className="min-h-screen bg-white"><Navbar />
      <div className="flex items-center justify-center py-32"><Loader2 size={28} className="animate-spin text-slate-400" /></div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-white"><Navbar />
      <div className="max-w-xl mx-auto py-24 text-center" data-testid="il-error">
        <h1 className="text-3xl font-bold text-slate-900">{error}</h1>
        <p className="text-slate-600 mt-3">Explore all our published industry × location pages, or start with the Lead Generation hub.</p>
        <Button asChild className="mt-6 bg-slate-900 hover:bg-slate-800 rounded-full">
          <Link to="/lead-generation">Back to Lead Generation</Link>
        </Button>
      </div>
      <Footer />
    </div>
  );

  const n = data?.narrative || {};

  return (
    <div className="min-h-screen bg-white" data-testid="il-page">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <div className="mb-3 flex items-center gap-2 text-xs text-slate-500 font-semibold uppercase tracking-widest">
          <Briefcase size={12} /><span data-testid="il-industry-chip">{data.industry_label}</span>
          <span className="text-slate-300">·</span>
          <MapPin size={12} /><span data-testid="il-location-chip">{data.location_label}</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-black text-slate-900 max-w-3xl" data-testid="il-headline">{n.hero_headline}</h1>
        <p className="text-lg text-slate-600 mt-4 max-w-3xl" data-testid="il-lede">{n.hero_lede}</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button asChild className="bg-slate-900 hover:bg-slate-800 rounded-full" data-testid="il-cta-audit">
            <Link to="/lead-generation/audit">Run my free audit <ArrowRight size={14} className="ml-1" /></Link>
          </Button>
          <Button asChild variant="outline" className="rounded-full" data-testid="il-cta-advisor">
            <Link to="/growth-advisor">Talk to Growth Advisor</Link>
          </Button>
        </div>

        <section className="mt-14 grid md:grid-cols-2 gap-4">
          <Card className="p-6 border border-slate-200 rounded-2xl md:col-span-2 bg-emerald-50/40 border-emerald-100">
            <div className="text-[11px] uppercase tracking-widest text-emerald-800 font-bold">Why this matters</div>
            <div className="text-slate-800 text-base mt-2" data-testid="il-why">{n.why_matters}</div>
          </Card>
          {(n.sections || []).map((s, i) => (
            <Card key={i} className="p-6 border border-slate-200 rounded-2xl" data-testid={`il-section-${i}`}>
              <div className="font-bold text-slate-900">{s.title}</div>
              <div className="text-sm text-slate-600 mt-2">{s.body}</div>
            </Card>
          ))}
        </section>

        <section className="mt-14">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Recommended for {data.industry_label} in {data.location_label}</div>
          <h2 className="text-2xl font-black text-slate-900 mt-2">Start with these services</h2>
          <div className="grid md:grid-cols-2 gap-3 mt-5">
            {(data.recommended_services || []).map((s) => (
              <Link key={s.slug} to={s.url} className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-slate-400 transition-colors" data-testid={`il-service-${s.slug}`}>
                <div className="flex-1 font-bold text-slate-900 capitalize">{s.slug.replace(/-/g, " ")}</div>
                <ArrowRight size={14} className="text-slate-400" />
              </Link>
            ))}
          </div>
        </section>

        <Card className="mt-16 p-6 border border-slate-200 rounded-2xl bg-slate-50/50 text-center">
          <div className="font-bold text-slate-900 text-xl">Ready to see what&apos;s holding you back?</div>
          <div className="text-sm text-slate-600 mt-2 max-w-lg mx-auto">Run a free Lead Generation Audit for your business — a 0-100 score, 7 sub-scores, and the top fixes tailored to {data.industry_label.toLowerCase()} in {data.location_label}.</div>
          <Button asChild className="mt-5 bg-slate-900 hover:bg-slate-800 rounded-full">
            <Link to="/lead-generation/audit">Get my audit</Link>
          </Button>
        </Card>
      </main>
      <Footer />
    </div>
  );
}
